import { useMemo } from 'react';
import { styled } from '@mui/material';

import { I18nResources, useI18n } from '@app-velocity/i18n';
import {
  InfoOutlineIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from '@app-velocity/icons';
import { Tooltip } from '@app-velocity/ui';

import { useReportContext } from '../../../../../../context/useReportContext';

const SingleMetricContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: `${theme.spacing(1)} 0`,
}));

const MetricData = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: theme.spacing(2),
}));

const ResultIcon = styled('div')<{ success: boolean }>(
  ({ theme, success }) => ({
    svg: {
      display: 'flex',
      height: theme.spacing(6),
      width: '100%',
      fill: success
        ? 'var(--success-success-icon-primary-color)'
        : 'var(--error-error-icon-primary-color)',
    },
  })
);

const TooltipButton = styled('div')({
  marginLeft: 'auto',
  svg: {
    fill: 'var(--text-text-color-primary)',
  },
});
const Details = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontWeight: 400,
  fontSize: 16,
  svg: {
    fill: '#1b1b1b',
  },
}));

const Required = styled('span')({
  color: '#969696',
  fontSize: 13,
  fontWeight: 400,
});

const Value = styled('span')(({ theme }) => ({
  fontWeight: 700,
  fontSize: 20,
}));

export const Metrics = () => {
  const { t } = useI18n();
  const { statistics, reportConfig } = useReportContext();
  const { config } = reportConfig;
  const { metrics } = statistics;
  const { dropOffs, avgDuration, missClicks, successRatio } = metrics;

  const prototypeMetrics: {
    value: string;
    unit: string;
    label: I18nResources;
    tooltip: I18nResources;
    required: string;
    success: boolean;
  }[] = useMemo(
    () =>
      config
        ? [
            {
              value: `${((successRatio || 0) * 100).toFixed(0)}`,
              unit: '%',
              label: 'prototypes:report.metric.success_rate',
              tooltip: 'prototypes:report.metric.success_rate_tooltip',
              success: (successRatio || 0) * 100 >= config.successRate,
              required: t('prototypes:report.metric.required', {
                value: `${config.successRate}%+`,
                interpolation: {},
              }),
            },
            {
              value: `${(avgDuration || 0).toFixed(0)}`,
              unit: 's',
              label: 'prototypes:report.metric.average_duration',
              tooltip: 'prototypes:report.metric.average_duration_tooltip',
              success: (avgDuration || 0) <= config.timeToSuccess,
              required: t('prototypes:report.metric.required', {
                value: `≤${config.timeToSuccess}s`,
                interpolation: {},
              }),
            },
            {
              value: `${((dropOffs || 0) * 100).toFixed(0)}`,
              unit: '%',
              label: 'prototypes:report.metric.drop_off_rate',
              tooltip: 'prototypes:report.metric.drop_off_rate_tooltip',
              success: (dropOffs || 0) * 100 <= config.dropOffRate,
              required: t('prototypes:report.metric.required', {
                value: `≤${config.dropOffRate}%`,
                interpolation: {},
              }),
            },
            {
              value: `${((missClicks || 0) * 100).toFixed(0)}`,
              unit: '%',
              label: 'prototypes:report.metric.missclick_rate',
              tooltip: 'prototypes:report.metric.missclick_rate_tooltip',
              success: (missClicks || 0) * 100 <= config.missclickRate,
              required: t('prototypes:report.metric.required', {
                value: `≤${config.missclickRate}%`,
                interpolation: {},
              }),
            },
          ]
        : [],
    [dropOffs, avgDuration, missClicks, config, successRatio, t]
  );

  return (
    <>
      {prototypeMetrics.map(
        ({ tooltip, required, success, unit, label, value }, index) => (
          <SingleMetricContainer key={index}>
            <ResultIcon success={success}>
              {success ? <ThumbsUpIcon /> : <ThumbsDownIcon />}
            </ResultIcon>
            <MetricData>
              <Value>
                {value}
                <small>{unit}</small>
              </Value>
              <Details>{t(label)}</Details>
              <Required>{required}</Required>
            </MetricData>
            <TooltipButton>
              <Tooltip title={t(tooltip)}>
                <InfoOutlineIcon />
              </Tooltip>
            </TooltipButton>
          </SingleMetricContainer>
        )
      )}
    </>
  );
};
